// Constants
import { StoreEntityState } from "@ms/uno-constants/lib/local/StoreConstants";
import { ContainerType } from "@ms/planner-models/lib/local/service/planner/enumerations/IPlannerEnumerations";
// Telemetry
import { TraceLevel } from "@ms/uno-telemetry/lib/local/events/Trace.event";
// Utilities
import { extractTeamIdAndChannelIdFromContainerId } from "@ms/uno-utilities/lib/local/TeamsUtilities";
import isEmpty from "lodash/isEmpty";
/**
 * Get container members from store
 * @param planContainer The plan container to get members for
 * @param stores Store instances
 * @param traceLogger Trace event logger
 */ export function getContainerMembersFromStores(planContainer, stores, traceLogger) {
    const { userStore, modernGroupsStore, rosterStore, teamsChannelStore, todoStore } = stores;
    switch(planContainer.containerType){
        case ContainerType.Group:
            const membersIds = modernGroupsStore.getModernGroupMembers(planContainer.containerId).data;
            return membersIds.map((memberId)=>userStore.getUserById(memberId)?.data).filter((user)=>user != null);
        case ContainerType.Roster:
            const rosterMemberIds = rosterStore.getRosterMembers(planContainer.containerId).data;
            return rosterMemberIds.map((memberId)=>userStore.getUserById(memberId)?.data).filter((user)=>user != null);
        case ContainerType.TeamsChannel:
            const teamsContainerUserIds = teamsChannelStore.getTeamsChannelMembers(planContainer.containerId).data;
            return teamsContainerUserIds.map((userId)=>userStore.getUserById(userId)?.data).filter((user)=>user != null);
        case ContainerType.ToDo:
            const todoMemberIds = todoStore.getTodoMembers(planContainer.containerId).data;
            return todoMemberIds.map((memberId)=>userStore.getUserById(memberId)?.data).filter((user)=>user != null);
        case ContainerType.User:
            const containerMember = userStore.getUserById(planContainer.containerId).data;
            return containerMember ? [
                containerMember
            ] : [];
        default:
            if (traceLogger) {
                traceLogger.logTrace(0x1e11d58f /* tag_4e3wp */ , TraceLevel.Warning, `ContainerUtilities - container type not supported [ContainerType=${planContainer.containerType}]`);
            }
            return [];
    }
}
/**
 * Get container display name from store
 * @param planContainer The plan container to get the name for
 * @param stores Store instances
 * @param traceLogger Trace event logger
 */ export function getContainerName(planContainer, stores, traceLogger) {
    const { modernGroupsStore, teamsChannelStore } = stores;
    switch(planContainer.containerType){
        case ContainerType.Group:
            const modernGroup = modernGroupsStore.getModernGroup(planContainer.containerId).data;
            return modernGroup?.displayName ?? "";
        case ContainerType.TeamsChannel:
            const { teamId: _, channelId } = extractTeamIdAndChannelIdFromContainerId(planContainer.containerId);
            const teamsChannel = teamsChannelStore.getChannel(channelId);
            return teamsChannel?.displayName ?? "";
        // We only display container name for group and teams channel plans for now by design. Leave the default as null for now.
        // We will revisit roster container's "shared" column text with PM in Wave2
        default:
            if (traceLogger) {
                traceLogger.logTrace(0x1e118751 /* tag_4ey3r */ , TraceLevel.Warning, `ContainerUtilities - Unsupported containerType [ContainerType=${planContainer.containerType}]`);
            }
            return "";
    }
}
/**
 * Get whether the given user is a member of the container. Returns null if membership cannot be determined.
 * @param userId The user id to check. If null, will return false
 * @param planContainer The plan container to check against
 * @param stores The stores to use for lookup
 * @param traceLogger Trace event logger
 */ export function getIsUserContainerMember(userId, planContainer, stores, traceLogger) {
    if (userId == null) {
        return false;
    }
    const containerMembers = getContainerMembersFromStores(planContainer, stores, traceLogger);
    const isContainerMember = containerMembers.some((member)=>member.id === userId);
    if (isContainerMember) {
        return true;
    }
    // If we couldn't find the user in the container members, theres a chance that the member data has not yet been fetched.
    if (isMembershipInfoSuccessfullyFetched(planContainer.containerId, planContainer.containerType, stores)) {
        // If membership data was successfully fetched, and user is not found, they are confirmed not a member
        return false;
    } else {
        // Membership cannot be determined if the data is not successfully fetched, return null for unknown
        return null;
    }
}
/**
 * Get whether the current user has access to the container. Returns null if access cannot be determined.
 * @param planContainer The plan container to check against
 * @param stores The stores to use for lookup
 * @param traceLogger Trace event logger
 */ export function canCurrentUserAccessContainer(planContainer, stores, traceLogger) {
    const { modernGroupsStore, rosterStore, teamsChannelStore, planStore } = stores;
    const currentUserId = stores.appContextStore.getAppContext().userId;
    if (currentUserId == null) {
        return false;
    }
    const isContainerMember = getIsUserContainerMember(currentUserId, planContainer, stores, traceLogger);
    if (isContainerMember !== null) {
        // If membership could be determined, return that value
        return isContainerMember;
    }
    // If membership cannot be determined, there are additional checks we can do specifically for current user
    // If the user is able to fetch the container, we can assume they have access to it.
    let storeStatus;
    switch(planContainer.containerType){
        case ContainerType.Group:
            storeStatus = modernGroupsStore.getModernGroup(planContainer.containerId).status.state;
            break;
        case ContainerType.Roster:
            storeStatus = rosterStore.getRoster(planContainer.containerId).status.state;
            break;
        case ContainerType.TeamsChannel:
            storeStatus = teamsChannelStore.getChannel(planContainer.containerId) != null ? StoreEntityState.Present : undefined;
            break;
        case ContainerType.ToDo:
            storeStatus = planStore.getPlan(planContainer.containerId).status.state;
            break;
        case ContainerType.User:
            // User container is always accessible to the user themselves
            return true;
        default:
            // Membership cannot be determined for other container types
            return null;
    }
    if (storeStatus === StoreEntityState.Present) {
        // If we can fetch the container, the user has access
        return true;
    } else if (storeStatus === StoreEntityState.Inaccessible) {
        // If we know the container failed to fetch with 403, the user does not have access
        return false;
    } else {
        // Access cannot be determined if the data is not fully fetched
        return null;
    }
}
/**
 * Get whether the given user is an owner of the container
 * @param userId The user id to check. If null, will return false
 * @param planContainer The plan container to check against
 * @param stores The stores to use for lookup
 */ export function getIsUserContainerOwner(userId, planContainer, stores) {
    const { modernGroupsStore } = stores;
    if (userId == null) {
        return false;
    }
    switch(planContainer.containerType){
        case ContainerType.Group:
            const ownerIds = modernGroupsStore.getModernGroupOwners(planContainer.containerId).data ?? [];
            return ownerIds.some((ownerId)=>ownerId === userId);
        case ContainerType.User:
            return planContainer.containerId === userId;
        default:
            // No ownership concept for other containers
            return false;
    }
}
/**
 * Determine whether the current user can access the plan via shared containers
 * @param sharedContainers The plan's sharedWithContainers
 * @param driveItemStore The drive item store
 */ export function canAccessPlanViaSharedContainers(sharedContainers, driveItemStore) {
    if (isEmpty(sharedContainers)) {
        return false;
    }
    return canAccessPlanViaDriveItemSharedContainer(sharedContainers, driveItemStore) || canAccessPlanViaOnlineMeetingSharedContainer(sharedContainers);
}
/**
 * Determine whether the current user can access the plan via drive item shared containers
 * @param sharedContainers The plan's sharedWithContainers
 * @param driveItemStore The drive item store
 */ export const canAccessPlanViaDriveItemSharedContainer = (sharedContainers, driveItemStore)=>{
    // Note: we currently assume all shared containers provide full access to the plan,
    // so we do not check container.accessLevel here
    const driveItemContainers = sharedContainers.filter((container)=>{
        return canUserAccessDriveItemSharedContainer(container, driveItemStore);
    });
    return !isEmpty(driveItemContainers);
};
/**
 * Helper method to get whether the user can access a specific drive item shared container
 * @param sharedContainer The plan's sharedWithContainer
 * @param driveItemStore The drive item store
 */ export const canUserAccessDriveItemSharedContainer = (sharedContainer, driveItemStore)=>{
    if (sharedContainer.containerType === ContainerType.DriveItem) {
        // containerId is in VROOM format for drive item containers
        if (isEmpty(sharedContainer.containerId)) {
            return false;
        }
        const storeState = driveItemStore.getDriveItem(sharedContainer.containerId).status.state;
        if (storeState === StoreEntityState.NotFound || storeState === StoreEntityState.Inaccessible) {
            // User cannot access the drive item
            return false;
        } else {
            // Default to true for permission to avoid UI flicker
            return true;
        }
    }
    return false;
};
/**
 * Determine whether the current user can access the plan via online meeting shared containers
 * @param sharedContainers The plan's sharedWithContainers
 */ export const canAccessPlanViaOnlineMeetingSharedContainer = (sharedContainers)=>{
    if (isEmpty(sharedContainers)) {
        return false;
    }
    return sharedContainers.some((container)=>{
        return container.containerType === ContainerType.OnlineMeeting;
    });
};
/**
 * Determine whether the current user can access the plan, either via host container or shared containers
 * @param plan The plan to check access for
 * @param stores The stores to use for lookup
 * @param traceLogger Trace event logger
 */ export function canAccessPlanViaContainers(plan, stores, traceLogger) {
    const { driveItemStore } = stores;
    // Check access via host container
    // Default to true if we cannot definitively determine access to avoid UI flicker
    const canAccessPlanViaHostContainer = canCurrentUserAccessContainer(plan.container, stores, traceLogger) ?? true;
    if (canAccessPlanViaHostContainer) {
        // User has access via host container, return true
        return true;
    }
    // If we cannot access via host container, check sharedWithContainers
    return canAccessPlanViaSharedContainers(plan.sharedWithContainers, driveItemStore);
}
/**
 * Return true if the user has access to the plan through shared containers only (not via host container)
 * @param plan The plan to check access for
 * @param stores The stores to use for lookup
 * @param traceLogger Trace event logger
 */ export function canAccessPlanViaSharedContainersOnly(plan, stores, traceLogger) {
    if (plan == null) {
        return false;
    }
    const { driveItemStore } = stores;
    const canAccessViaHostContainer = canCurrentUserAccessContainer(plan.container, stores, traceLogger) ?? true;
    return canAccessPlanViaSharedContainers(plan.sharedWithContainers, driveItemStore) && !canAccessViaHostContainer;
}
/**
 * Get container members store state from store
 * @param containerId The id of the container
 * @param containerType The type of the container
 * @param stores The stores to use for lookup
 */ export function getContainerMembersStoreState(containerId, containerType, stores) {
    const { userStore, modernGroupsStore, rosterStore, teamsChannelStore, todoStore } = stores;
    switch(containerType){
        case ContainerType.Group:
            return modernGroupsStore.getModernGroupMembers(containerId).status.state;
        case ContainerType.Roster:
            return rosterStore.getRosterMembers(containerId).status.state;
        case ContainerType.TeamsChannel:
            return teamsChannelStore.getTeamsChannelMembers(containerId).status.state;
        case ContainerType.ToDo:
            return todoStore.getTodoMembers(containerId).status.state;
        case ContainerType.User:
            return userStore.getUserById(containerId).status.state;
        default:
            return null;
    }
}
/**
 * Helper method to determine if membership info has been successfully fetched for a container
 * @param containerId The id of the container
 * @param containerType The type of the container
 * @param stores The stores to use for lookup
 */ function isMembershipInfoSuccessfullyFetched(containerId, containerType, stores) {
    const memberStoreState = getContainerMembersStoreState(containerId, containerType, stores);
    if (memberStoreState == null) {
        // Membership cannot be determined for other container types
        return false;
    }
    // If the member data is fetched successfully, return true. Otherwise if absent, fetching, or failed, return false.
    return memberStoreState === StoreEntityState.Present;
}
export function getContainerSensitivityLabelId(plan, stores) {
    const containerId = plan.container.containerId;
    const containerType = plan.container.containerType;
    switch(containerType){
        case ContainerType.Group:
            // For Group container, return the sensitivity label ID from the Modern Group
            const modernGroupSensitivityLabelId = stores.modernGroupsStore.getModernGroup(containerId).data?.assignedSensitivityLabelId ?? null;
            return modernGroupSensitivityLabelId;
        case ContainerType.Roster:
            const rosterSensitivityLabel = stores.rosterStore.getRoster(containerId).data?.assignedSensitivityLabel ?? null;
            return rosterSensitivityLabel?.sensitivityLabelId ?? null;
        default:
            // For other containers, we currently do not have sensitivity label info, return null
            return null;
    }
}
